import type { Room } from 'livekit-client';

import type { MicStatus } from '../core/logic/mic-availability.js';
import type { ServiceMode } from '../core/types/service-mode.js';
import type { TranscriptionSegment } from '../core/types/transcription.js';

/**
 * Agent lifecycle state surfaced by the SDK. Mirrors the values published by
 * the LiveKit agents runtime on the `lk.agent.state` participant attribute,
 * with `'disconnected'` added for when the agent participant leaves the room.
 */
export type AgentState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'disconnected';

/**
 * Payload from the `tool-effects` data channel. The effect name and data shape
 * are defined by the Python agent's tool handlers; the SDK forwards both verbatim.
 */
export interface ToolEffect {
  effect: string;
  data: Record<string, unknown>;
}

export type { MicStatus };
export type {
  TranscriptionSegment,
  TranscriptionSpeaker,
} from '../core/types/transcription.js';

/**
 * Thin wrapper around a LiveKit video track published by the agent.
 * Call `getElement()` once to retrieve the underlying `HTMLVideoElement`;
 * canvas games can pass that element to `PIXI.Texture.from(...)` for a
 * zero-copy GPU path. `detach()` cleans up when the track goes away and is
 * terminal — calling `getElement()` after `detach()` throws.
 */
export interface DealerVideoTrack {
  getElement(): HTMLVideoElement;
  /** Terminal cleanup. After this, `getElement()` throws. */
  detach(): void;
}

/**
 * One-shot avatar-worker outcome (avatar-enabled sessions only). `active` = the
 * avatar worker joined and video is imminent; `unavailable` = no worker joined
 * within the agent's watch window (provider billing/outage/misconfig). Lets a
 * canvas consumer distinguish "audio-only by design" (never emitted) from
 * "avatar configured but failed" (emitted `unavailable`).
 */
export type AvatarStatus = 'active' | 'unavailable';

/** LiveKit transport reconnection transitions surfaced to the consumer. */
export type ConnectionStateValue = 'reconnecting' | 'reconnected';

/**
 * Synchronous snapshot of SDK state. Use this immediately after subscribing
 * to late-arriving events (e.g. `videoTrack`) to pick up anything that fired
 * before the listener was attached.
 */
export interface SDKStateSnapshot {
  connected: boolean;
  agentState: AgentState;
  serviceMode: ServiceMode;
  muted: boolean;
  audioAttached: boolean;
  /** Whether the browser currently permits autoplay of the dealer's TTS audio.
   * `false` = a user gesture via `startAudio()` is required to unblock playback. */
  canPlaybackAudio: boolean;
  /** Microphone availability from the most recent PTT acquisition attempt. */
  micStatus: MicStatus;
  videoTrack: DealerVideoTrack | null;
  avatarStatus: AvatarStatus | null;
}

/** Thrown by `connect()` / `initialize()` when the SDK is already connected. */
export class AlreadyConnectedError extends Error {
  readonly code = 'ALREADY_CONNECTED' as const;
  constructor() {
    super('DealerSDK is already connected; call destroy() first');
    this.name = 'AlreadyConnectedError';
  }
}

export interface SDKLogger {
  debug(msg: string, meta?: unknown): void;
  info(msg: string, meta?: unknown): void;
  warn(msg: string, meta?: unknown): void;
  error(msg: string, meta?: unknown): void;
}

/**
 * Configuration for all SDK entry points (DealerSDK, BlackjackSDKService, RouletteSDKService).
 *
 * Mobile Safari autoplay note: call `startAudio()` from a user-gesture handler
 * (click/tap) after `connect()` / `initialize()` resolves — otherwise the agent's
 * TTS audio will not play on iOS.
 *
 * `serviceUrl` is required, and there is no build-time fallback in either mode:
 * iframe `AIServiceConfig` requires `serviceUrl` AND `webClientUrl` as of
 * web-client 0.18.0 (ADR 0046 — configuration binds at embed time or runtime,
 * never at build time). Migrating from iframe mode means dropping
 * `containerId`/`webClientUrl`, not adding `serviceUrl`.
 */
export interface SDKServiceConfig {
  sessionToken: string;
  serviceUrl: string;
  initialMuted?: boolean;
  audioContainer?: HTMLElement;
  logger?: SDKLogger;
  roomFactory?: () => Room;
  agentIdentityPrefix?: string;
}

/**
 * Events emitted by DealerSDK and re-emitted by the Service wrappers.
 *
 * Error policy: setup failures (from `connect()` / `initialize()`) reject the
 * returned Promise. Mid-session failures emit the `error` event. The same
 * failure is never reported on both channels.
 *
 * `disconnected` signals a TRANSPORT-initiated disconnect (network drop, server
 * kick, room finished) observed while the SDK is live — emitted by the
 * RoomEvent.Disconnected handler. Caller-initiated teardown via `destroy()` is
 * SILENT: it unbinds the handler before disconnecting, so no `disconnected` is
 * emitted (the caller already knows it tore down). The graceful `disconnect()`
 * stays observable and DOES emit `disconnected`.
 */
export interface SDKEventMap {
  ready: AgentState;
  agentState: AgentState;
  serviceMode: ServiceMode;
  toolEffect: ToolEffect;
  videoTrack: DealerVideoTrack;
  speaking: boolean;
  mute: { muted: boolean };
  disconnected: { reason?: string };
  error: { code: string; message: string; cause?: unknown };
  /** LiveKit reconnection cycle (informational; the transport self-heals). */
  connectionState: { state: ConnectionStateValue };
  /** Browser autoplay permission for the dealer's TTS audio changed. */
  audioPlaybackChanged: { canPlayback: boolean };
  /** Avatar-worker outcome for avatar-enabled sessions (one-shot per session). */
  avatarStatus: { status: AvatarStatus };
  /** Live caption update (dealer speech + player STT). See TranscriptionSegment. */
  transcription: TranscriptionSegment;
  /**
   * Microphone availability changed. Fires when a PTT hold's mic acquisition
   * classifies differently than before ('ok' → 'blocked', …) and when the player
   * grants the permission in browser settings ('blocked' → 'ok'). Emitted on
   * change only; read the current value from `getState().micStatus`.
   */
  micStatus: { status: MicStatus };
  /**
   * A game event was handed to the transport (before the RPC settles). Lets
   * state-layer consumers (e.g. the widget suspension tracker) observe the
   * outbound game-event flow without wrapping every send* method.
   */
  gameEventSent: { type: string; data?: unknown };
}

export type SDKEventName = keyof SDKEventMap;

/**
 * Runtime list of every `SDKEventMap` key. Needed because `SDKEventMap` is a
 * compile-time-only type; Service wrappers iterate over this array to forward
 * events from the internal `DealerSDK` up to themselves.
 *
 * Keep in sync with `SDKEventMap` above. Adding a key without updating this
 * array silently drops that event for Service-wrapper consumers.
 */
export const SDK_EVENT_NAMES: readonly SDKEventName[] = [
  'ready',
  'agentState',
  'serviceMode',
  'toolEffect',
  'videoTrack',
  'speaking',
  'mute',
  'disconnected',
  'error',
  'connectionState',
  'audioPlaybackChanged',
  'avatarStatus',
  'transcription',
  'micStatus',
  'gameEventSent',
] as const;
